import React, { useState } from 'react';

const getSvgSize = (svg) => {
  const vb = (svg.getAttribute('viewBox') || '').trim().split(/\s+/).map(Number);
  if (vb.length === 4 && vb[2] > 0 && vb[3] > 0) return { width: vb[2], height: vb[3] };
  return { width: svg.clientWidth || 848, height: svg.clientHeight || 240 };
};

const svgToDataUri = (svg) => {
  const svgString = new XMLSerializer().serializeToString(svg);
  const base64 = btoa(unescape(encodeURIComponent(svgString)));
  return 'data:image/svg+xml;base64,' + base64;
};

const renderSvgToPngBlob = (svg) => new Promise((resolve, reject) => {
  const { width, height } = getSvgSize(svg);
  const scale = 2; // rendu net (façon écran retina)
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Échec de la génération PNG.')), 'image/png');
  };
  img.onerror = () => reject(new Error("Impossible de charger l'image SVG."));
  img.src = svgToDataUri(svg);
});

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Barre d'actions au-dessus d'un graphique SVG : copier en PNG, télécharger en PNG,
 * copier les valeurs sous-jacentes en TSV (collable dans Excel/Sheets).
 * @param {string} containerId - id du conteneur DOM contenant le <svg>
 * @param {() => string} getDataTSV - retourne les valeurs du graphique au format TSV
 */
const ChartExportToolbar = ({ containerId, getDataTSV }) => {
  const [status, setStatus] = useState(null); // { key, kind, label }

  const flash = (key, kind, label) => {
    setStatus({ key, kind, label });
    setTimeout(() => setStatus(s => (s?.key === key ? null : s)), 2200);
  };

  const getSvg = () => document.getElementById(containerId)?.querySelector('svg');

  const copyImage = async () => {
    const svg = getSvg();
    if (!svg) return flash('copy', 'fail', 'Rien à copier');
    try {
      const blob = await renderSvgToPngBlob(svg);
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      flash('copy', 'ok', '✓ Copié !');
    } catch (err) {
      flash('copy', 'fail', 'Échec — réessaie');
    }
  };

  const downloadImage = async () => {
    const svg = getSvg();
    if (!svg) return flash('download', 'fail', 'Rien à télécharger');
    try {
      const blob = await renderSvgToPngBlob(svg);
      downloadBlob(blob, containerId + '.png');
      flash('download', 'ok', '✓ Téléchargé !');
    } catch (err) {
      flash('download', 'fail', 'Échec — réessaie');
    }
  };

  const copyData = async () => {
    const text = getDataTSV();
    if (!text) return flash('data', 'fail', 'Aucune donnée');
    try {
      await navigator.clipboard.writeText(text);
      flash('data', 'ok', '✓ Copié !');
    } catch (err) {
      flash('data', 'fail', 'Échec — réessaie');
    }
  };

  const btnClass = (key) => `rw-copy-chart-btn ${status?.key === key ? (status.kind === 'ok' ? 'rw-copy-ok' : 'rw-copy-fail') : ''}`;

  return (
    <div className="rw-chart-toolbar">
      <button type="button" className={btnClass('copy')} onClick={copyImage}>
        {status?.key === 'copy' ? status.label : '📋 Copier'}
      </button>
      <button type="button" className={btnClass('download')} onClick={downloadImage}>
        {status?.key === 'download' ? status.label : '💾 Télécharger'}
      </button>
      <button type="button" className={btnClass('data')} onClick={copyData}>
        {status?.key === 'data' ? status.label : '🔢 Valeurs'}
      </button>
    </div>
  );
};

export default ChartExportToolbar;
